import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiFetch } from '../bizsuite-app';

/**
 * Floating chat bubble — Phase 1.
 *
 * Mounts once in Layout.jsx. Renders a bottom-right pill + expandable panel.
 * Sends /api/ai/chat (Ollama behind it).
 *
 * Phases beyond 1:
 *   - Tool calls for data Q&A (with RBAC filtering)
 *   - NL-to-SQL via a read-only DB role
 *   - RAG over uploaded docs
 */
export default function ChatBubble({ currentUser }) {
  const [open, setOpen]         = useState(false);
  const [messages, setMessages] = useState([]); // {role, content}
  const [input, setInput]       = useState('');
  const [sending, setSending]   = useState(false);
  const [hasUnread, setHasUnread] = useState(false); // arrived while minimized
  const [health, setHealth]     = useState(null); // null|'ready'|'down'|'no-model'
  const bodyRef = useRef(null);

  /* Clear unread when the user opens the panel */
  useEffect(() => { if (open) setHasUnread(false); }, [open]);

  /* Health check on first open */
  useEffect(() => {
    if (!open || health) return;
    apiFetch('/api/ai/chat/health').then(r => r.json()).then(d => {
      setHealth(d.status || 'down');
      if (messages.length === 0) {
        const hello = d.status === 'ready'
          ? `Hi ${currentUser?.fullName?.split(' ')[0] || ''}! I'm Nova, your BizSuite assistant. Ask about employees, pipeline, customers, or anything in the app. (I can look up employees, opportunities, leads, customers and more — scoped to your permissions.)`.trim()
          : d.status === 'no-model'
            ? 'The AI backend is reachable but no Phi-3 mini model is loaded. Run scripts/install-ollama.sh on the server.'
            : 'The AI backend is not reachable right now. The Ollama service may be down.';
        setMessages([{ role: 'assistant', content: hello }]);
      }
    }).catch(() => setHealth('down'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* Auto-scroll on new message */
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, sending]);

  /* Use a ref to track panel openness at response time — because we want to
     mark hasUnread only if the user minimized WHILE the request was in flight. */
  const openRef = useRef(open);
  useEffect(() => { openRef.current = open; }, [open]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    const nextMsgs = [...messages, { role: 'user', content: text }];
    setMessages(nextMsgs);
    setSending(true);
    try {
      const res = await apiFetch('/api/ai/chat', {
        method: 'POST',
        body: JSON.stringify({ messages: nextMsgs }),
      });
      let reply;
      if (res.ok) {
        const d = await res.json();
        reply = { role: 'assistant', content: d.reply || '(empty)' };
      } else {
        const d = await res.json().catch(() => ({}));
        reply = { role: 'assistant', content: `Error: ${d.error || 'chat failed'}`, _err: true };
      }
      setMessages(m => [...m, reply]);
      if (!openRef.current) setHasUnread(true);
    } catch (e) {
      setMessages(m => [...m, { role: 'assistant', content: `Network error: ${e.message}`, _err: true }]);
      if (!openRef.current) setHasUnread(true);
    }
    setSending(false);
  }, [input, sending, messages]);

  /* Collapsed bubble — pulse ring while thinking, red dot for unread */
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={bubbleBtn}
        title={sending ? 'Nova is thinking…' : (hasUnread ? 'Nova replied — click to view' : 'Ask Nova — BizSuite AI')}
      >
        {sending && <span style={pulseRing} />}
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'relative', zIndex: 1 }}>
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
        {hasUnread && <span style={unreadDot} />}
        <style>{`
          @keyframes novaPulse {
            0%   { transform: scale(1);   opacity: 0.6; }
            70%  { transform: scale(1.6); opacity: 0;   }
            100% { transform: scale(1.6); opacity: 0;   }
          }
        `}</style>
      </button>
    );
  }

  /* Expanded panel */
  return (
    <div style={panel}>
      <div style={header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ ...statusDot, background: health === 'ready' ? '#10B981' : health === 'down' ? '#EF4444' : '#F59E0B' }}></span>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Nova</span>
          <span style={{ fontSize: 10.5, color: '#94A3B8' }}>Phi-3 mini · local</span>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={() => setMessages([])} style={hdrBtn} title="Clear conversation">⟲</button>
          <button
            onClick={() => setOpen(false)}
            style={hdrBtn}
            title={sending ? 'Minimize — Nova will keep working and notify you when ready' : 'Minimize'}
          >–</button>
        </div>
      </div>
      <div ref={bodyRef} style={body}>
        {messages.length === 0 && <div style={{ color: '#64748B', fontSize: 12, padding: 8 }}>Loading…</div>}
        {messages.map((m, i) => (
          <div key={i} style={{
            alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
            background: m.role === 'user' ? 'rgba(59,143,244,0.14)' : (m._err ? 'rgba(239,68,68,0.10)' : 'rgba(255,255,255,0.04)'),
            color:      m._err ? '#FCA5A5' : '#E5E7EB',
            border: m.role === 'user' ? '1px solid rgba(59,143,244,0.28)' : '1px solid rgba(255,255,255,0.06)',
            borderRadius: 12,
            padding: '8px 12px',
            maxWidth: '85%',
            fontSize: 13,
            lineHeight: 1.45,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}>{m.content}</div>
        ))}
        {sending && (
          <div style={{
            alignSelf: 'flex-start', color: '#94A3B8', fontSize: 12,
            display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
          }}>
            <span className="chat-dot"></span><span className="chat-dot"></span><span className="chat-dot"></span>
            <style>{`
              .chat-dot { width:5px; height:5px; border-radius:50%; background:#94A3B8; animation: cbBlink 1.4s infinite both; }
              .chat-dot:nth-child(2) { animation-delay: 0.2s; }
              .chat-dot:nth-child(3) { animation-delay: 0.4s; }
              @keyframes cbBlink { 0%, 80%, 100% { opacity:0.2 } 40% { opacity:1 } }
            `}</style>
          </div>
        )}
      </div>
      <form style={footer} onSubmit={e => { e.preventDefault(); send(); }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Ask Nova…"
          disabled={sending}
          style={chatInput}
          autoFocus
        />
        <button type="submit" disabled={sending || !input.trim()} style={sendBtn}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
          </svg>
        </button>
      </form>
    </div>
  );
}

/* ── styles ── */
const bubbleBtn = {
  position: 'fixed', right: 22, bottom: 22, zIndex: 9998,
  width: 52, height: 52, borderRadius: '50%',
  border: '1px solid rgba(255,0,229,0.35)',
  background: 'linear-gradient(135deg, #FF00E5 0%, #BF5FFF 100%)',
  color: '#fff', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  boxShadow: '0 6px 20px rgba(255,0,229,0.35), 0 2px 6px rgba(0,0,0,0.4)',
  transition: 'transform 0.15s ease',
  position: 'fixed',
  overflow: 'visible',
};
/* Expanding ring rendered behind the bubble while a request is in flight */
const pulseRing = {
  position: 'absolute', inset: -4,
  borderRadius: '50%',
  border: '2px solid rgba(255,0,229,0.55)',
  animation: 'novaPulse 1.4s ease-out infinite',
  pointerEvents: 'none',
};
/* Small red dot shown when a reply arrived while Nova was minimized */
const unreadDot = {
  position: 'absolute', top: 4, right: 4,
  width: 12, height: 12, borderRadius: '50%',
  background: '#EF4444',
  border: '2px solid rgba(11,15,23,0.98)',
  boxShadow: '0 0 0 1px rgba(239,68,68,0.4)',
};
const panel = {
  position: 'fixed', right: 22, bottom: 22, zIndex: 9999,
  width: 380, height: 540, maxHeight: 'calc(100vh - 44px)',
  background: 'rgba(11,15,23,0.98)',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 14, backdropFilter: 'blur(10px)',
  boxShadow: '0 16px 48px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,0,229,0.12)',
  display: 'flex', flexDirection: 'column', overflow: 'hidden',
};
const header = {
  padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  background: 'linear-gradient(180deg, rgba(255,0,229,0.06), transparent)',
};
const statusDot = { width: 8, height: 8, borderRadius: '50%' };
const hdrBtn = {
  width: 24, height: 24, borderRadius: 5, padding: 0,
  border: '1px solid rgba(255,255,255,0.08)', background: 'transparent',
  color: '#94A3B8', cursor: 'pointer', fontSize: 14,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};
const body = {
  flex: 1, overflowY: 'auto', padding: 12,
  display: 'flex', flexDirection: 'column', gap: 8,
};
const footer = {
  padding: 10, borderTop: '1px solid rgba(255,255,255,0.08)',
  display: 'flex', gap: 8, alignItems: 'center',
};
const chatInput = {
  flex: 1, padding: '8px 12px', borderRadius: 7,
  border: '1px solid rgba(255,255,255,0.12)',
  background: 'rgba(255,255,255,0.04)', color: '#E5E7EB', fontSize: 13,
  outline: 'none',
};
const sendBtn = {
  width: 34, height: 34, padding: 0, borderRadius: 7,
  border: '1px solid rgba(255,0,229,0.35)',
  background: 'linear-gradient(135deg, #FF00E5 0%, #BF5FFF 100%)',
  color: '#fff', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};
